import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
  keyframes,
} from "@mui/material";
import LockRounded from "@mui/icons-material/LockRounded";
import { useNavigate } from "@tanstack/react-router";
import { useTranslate } from "../i18n";
import { useAuthStore } from "../stores/auth-store";
import { renderGoogleSignInButton } from "../lib/google-identity";

const driftOne = keyframes`
  0%, 100% { transform: translate3d(-14%, -8%, 0) scale(.96); opacity: .38; }
  50% { transform: translate3d(24%, 16%, 0) scale(1.2); opacity: .72; }
`;

const driftTwo = keyframes`
  0%, 100% { transform: translate3d(14%, 12%, 0) scale(1.12); opacity: .3; }
  50% { transform: translate3d(-24%, -18%, 0) scale(.88); opacity: .66; }
`;

const breathe = keyframes`
  0%, 100% { transform: translate3d(-50%, -50%, 0) scale(.86); opacity: .2; }
  50% { transform: translate3d(-44%, -54%, 0) scale(1.25); opacity: .58; }
`;

const smokeFlow = keyframes`
  0% { transform: translate3d(-7%, 4%, 0) rotate(-4deg) scale(.96); opacity: .32; }
  50% { transform: translate3d(6%, -5%, 0) rotate(3deg) scale(1.08); opacity: .7; }
  100% { transform: translate3d(-2%, -10%, 0) rotate(-1deg) scale(1.02); opacity: .42; }
`;

function PdfToMagazineIcon() {
  return <svg
    width="52"
    height="27"
    viewBox="0 0 66 34"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4.5 3.5h14l5 5v21h-19v-26Z"
      fill="rgba(255,255,255,.06)"
      stroke="#FCA5A5"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M18.5 3.5v5h5"
      stroke="#FCA5A5"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <rect x="1" y="15" width="20" height="9" rx="3" fill="#EF4444" />
    <text
      x="11"
      y="21.5"
      fill="white"
      fontSize="6.5"
      fontWeight="800"
      textAnchor="middle"
      fontFamily="Inter, sans-serif"
    >
      PDF
    </text>
    <path
      d="M28 17h8m-3-3 3 3-3 3"
      stroke="#C7D2FE"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M41 7.5c5.2 0 8.5 1.1 10 3.2v18.1c-1.5-2-4.8-3.1-10-3.1V7.5Z"
      fill="rgba(99,102,241,.32)"
      stroke="#C7D2FE"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M61 7.5c-5.2 0-8.5 1.1-10 3.2v18.1c1.5-2 4.8-3.1 10-3.1V7.5Z"
      fill="rgba(59,130,246,.26)"
      stroke="#C7D2FE"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M51 11v17.5" stroke="#A5B4FC" strokeWidth="1.2" />
    <path
      d="M44.5 12h3.5m-3.5 3h3.5m6 0h3.5m-3.5 3h3.5"
      stroke="rgba(224,231,255,.72)"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
  </svg>;
}

function UserIcon() {
  return <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="userIconGrad" x1="3" y1="2" x2="21" y2="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#e0e7ff" />
        <stop offset="100%" stopColor="#818cf8" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="8.2" r="4" fill="url(#userIconGrad)" />
    <path
      d="M4 20.2c0-4.42 3.58-7.2 8-7.2s8 2.78 8 7.2"
      stroke="url(#userIconGrad)"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </svg>;
}

export function LoginPage() {
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement>(null);
  const termsAcceptedRef = useRef(false);
  const [buttonError, setButtonError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsError, setTermsError] = useState("");
  const { user, loading, error, completeGoogleSignIn } = useAuthStore();
  const __ = useTranslate();

  function updateTermsAccepted(accepted: boolean) {
    termsAcceptedRef.current = accepted;
    setTermsAccepted(accepted);
    if (accepted) setTermsError("");
  }

  useEffect(() => {
    if (user) void navigate({ to: "/" });
  }, [user, navigate]);
  useEffect(() => {
    if (!buttonRef.current || user) return;
    let active = true;
    void renderGoogleSignInButton(buttonRef.current, (credential) => {
      if (!active) return;
      if (!termsAcceptedRef.current) {
        setTermsError(__("กรุณายอมรับเงื่อนไขก่อนเข้าสู่ระบบ"));
        return;
      }
      void completeGoogleSignIn(credential);
    }).catch((renderError) => {
      if (active) {
        setButtonError(renderError instanceof Error ? renderError.message : __("Google Sign-In failed to load"));
      }
    });
    return () => {
      active = false;
    };
  }, [__, completeGoogleSignIn, user]);

  return <Box
    component="main"
    sx={{
      position: "relative",
      minHeight: "100vh",
      overflow: "hidden",
      display: "grid",
      placeItems: "center",
      p: { xs: 2, sm: 3 },
      color: "#fafafa",
      bgcolor: "#08090c",
      isolation: "isolate",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        zIndex: -3,
        background: `
          radial-gradient(circle at 50% 115%, rgba(99,102,241,.19), transparent 42%),
          linear-gradient(180deg, #08090c 0%, #0b0c12 54%, #08090c 100%)
        `,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        zIndex: 2,
        pointerEvents: "none",
        opacity: 0.18,
        backgroundImage:
          "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.02) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        maskImage: "linear-gradient(to bottom, transparent, black 28%, black 72%, transparent)",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "& .smoke-orb, & .smoke-flow": { animation: "none !important" },
      },
    }}
  >
    <Box
      className="smoke-flow"
      aria-hidden="true"
      sx={{
        position: "absolute",
        zIndex: -2,
        inset: "-25%",
        filter: "blur(58px)",
        willChange: "transform, opacity",
        background: `
          radial-gradient(ellipse at 22% 34%, rgba(109,40,217,.5) 0%, rgba(79,70,229,.2) 26%, transparent 53%),
          radial-gradient(ellipse at 76% 62%, rgba(37,99,235,.44) 0%, rgba(14,116,144,.16) 28%, transparent 54%),
          radial-gradient(ellipse at 52% 48%, rgba(148,163,184,.2) 0%, rgba(99,102,241,.12) 25%, transparent 48%)
        `,
        animation: `${smokeFlow} 8s ease-in-out infinite`,
      }}
    />
    <Box
      className="smoke-orb"
      aria-hidden="true"
      sx={{
        position: "absolute",
        zIndex: -2,
        width: { xs: 420, md: 720 },
        height: { xs: 420, md: 720 },
        left: { xs: "-48%", md: "-12%" },
        top: { xs: "-12%", md: "-38%" },
        borderRadius: "50%",
        filter: "blur(72px)",
        background:
          "radial-gradient(circle at 55% 55%, rgba(76,29,149,.75), rgba(67,56,202,.22) 48%, transparent 72%)",
        willChange: "transform, opacity",
        animation: `${driftOne} 10s ease-in-out infinite`,
      }}
    />
    <Box
      className="smoke-orb"
      aria-hidden="true"
      sx={{
        position: "absolute",
        zIndex: -2,
        width: { xs: 480, md: 760 },
        height: { xs: 480, md: 760 },
        right: { xs: "-62%", md: "-18%" },
        bottom: { xs: "-22%", md: "-48%" },
        borderRadius: "50%",
        filter: "blur(86px)",
        background:
          "radial-gradient(circle at 45% 45%, rgba(37,99,235,.72), rgba(14,116,144,.18) 50%, transparent 72%)",
        willChange: "transform, opacity",
        animation: `${driftTwo} 12s ease-in-out infinite`,
      }}
    />
    <Box
      className="smoke-orb"
      aria-hidden="true"
      sx={{
        position: "absolute",
        zIndex: -1,
        width: { xs: 360, md: 560 },
        height: { xs: 240, md: 360 },
        left: "50%",
        top: "48%",
        borderRadius: "50%",
        filter: "blur(90px)",
        background:
          "radial-gradient(ellipse, rgba(148,163,184,.18), rgba(99,102,241,.13) 45%, transparent 72%)",
        transform: "translate(-50%, -50%)",
        willChange: "transform, opacity",
        animation: `${breathe} 7s ease-in-out infinite`,
      }}
    />

    <Card
      variant="outlined"
      sx={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 438,
        overflow: "visible",
        borderRadius: { xs: 4, sm: 5 },
        borderColor: "rgba(255,255,255,.13)",
        color: "inherit",
        bgcolor: "rgba(13,14,20,.72)",
        backgroundImage:
          "linear-gradient(145deg, rgba(255,255,255,.055), rgba(255,255,255,.012) 45%, rgba(99,102,241,.035))",
        boxShadow:
          "0 36px 100px rgba(0,0,0,.58), inset 0 1px 0 rgba(255,255,255,.055)",
        backdropFilter: "blur(24px) saturate(125%)",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: -1,
          zIndex: -1,
          borderRadius: "inherit",
          background:
            "linear-gradient(135deg, rgba(255,255,255,.16), transparent 34%, transparent 68%, rgba(99,102,241,.18))",
          opacity: 0.45,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
        <Box
          sx={{
            mx: "auto",
            mb: 3,
            width: 84,
            height: 84,
            display: "grid",
            placeItems: "center",
            position: "relative",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,.16)",
            background:
              "linear-gradient(150deg, rgba(129,140,248,.32), rgba(255,255,255,.04) 60%)",
            boxShadow:
              "0 16px 40px rgba(49,46,129,.32), inset 0 1px 0 rgba(255,255,255,.12)",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: -6,
              borderRadius: "50%",
              border: "1px solid rgba(129,140,248,.22)",
            },
          }}
        >
          <UserIcon />
        </Box>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 30, sm: 36 },
            fontWeight: 750,
            letterSpacing: "-.035em",
            lineHeight: 1.12,
            backgroundImage:
              "linear-gradient(135deg, #fafafa 35%, #c7d2fe 78%, #a5b4fc)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          E-Mag Studio
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            mb: 4,
            color: "rgba(226,232,240,.62)",
            fontSize: 15,
            lineHeight: 1.7,
          }}
        >
          {__("ระบบสร้าง E-Magazine")}
        </Typography>

        <Box
          sx={{
            mt: 2,
            mb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.75,
              py: 0.75,
            }}
          >
            <PdfToMagazineIcon />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: ".04em",
                color: "rgba(226,232,240,.72)",
                whiteSpace: "nowrap",
              }}
            >
              {__("PDF to E-Magazine")}
            </Typography>
          </Box>
        </Box>

        {(error || buttonError || termsError) && (
          <Alert
            severity="error"
            sx={{
              mb: 2.5,
              textAlign: "left",
              color: "#fecaca",
              border: "1px solid rgba(248,113,113,.24)",
              bgcolor: "rgba(127,29,29,.22)",
              "& .MuiAlert-icon": { color: "#fca5a5" },
            }}
          >
            {error ?? buttonError ?? termsError}
          </Alert>
        )}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
            gap: 1.1,
            textAlign: "center",
          }}
        >
          <Checkbox
            checked={termsAccepted}
            onChange={(event) => updateTermsAccepted(event.target.checked)}
            inputProps={{ "aria-label": __("ยอมรับเงื่อนไขและข้อตกลงในการใช้บริการ") }}
            sx={{
              mt: -0.5,
              p: 0.25,
              color: "rgba(226,232,240,.64)",
              "&.Mui-checked": { color: "#a5b4fc" },
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: "rgba(226,232,240,.74)", lineHeight: 1.6 }}
          >
            {__("ฉันยอมรับ")}{" "}
            <Link
              component="button"
              type="button"
              onClick={() => setTermsOpen(true)}
              sx={{
                p: 0,
                border: 0,
                color: "#c7d2fe",
                font: "inherit",
                fontWeight: 700,
                textDecorationColor: "rgba(199,210,254,.45)",
                verticalAlign: "baseline",
                cursor: "pointer",
              }}
            >
              {__("เงื่อนไขและข้อตกลงในการใช้บริการ")}
            </Link>
          </Typography>
        </Box>
        <Box
          sx={{
            position: "relative",
            minHeight: 44,
            display: "grid",
            placeItems: "center",
            opacity: loading || !termsAccepted ? 0.45 : 1,
            pointerEvents: loading || !termsAccepted ? "none" : "auto",
            transition: "opacity .2s ease",
          }}
          aria-disabled={loading || !termsAccepted}
        >
          {loading && (
            <CircularProgress
              aria-label={__("กำลังเข้าสู่ระบบ")}
              size={24}
              sx={{ position: "absolute", color: "#a5b4fc" }}
            />
          )}
          <Box
            ref={buttonRef}
            data-testid="google-signin-button"
            aria-label={__("Sign in with Google")}
            sx={{
              width: "100%",
              minHeight: 44,
              display: "grid",
              placeItems: "center",
              "& > div": { maxWidth: "100%" },
            }}
          />
        </Box>

          <Typography
            variant="caption"
            sx={{
              mt: 4,
              color: "rgba(148,163,184,.48)",
              lineHeight: 1.6,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
            }}
          >
            <LockRounded sx={{ fontSize: 15, color: "#a5b4fc" }} />
            {__("Secure access powered by Google")}
          </Typography>
      </CardContent>
    </Card>
    <Dialog
      open={termsOpen}
      onClose={() => setTermsOpen(false)}
      aria-labelledby="terms-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="terms-dialog-title">{__("เงื่อนไขและข้อตกลงในการใช้บริการ")}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ color: "text.secondary" }}>
          <Typography component="p" variant="body2" mb={1.5}>
            {__("การใช้งาน Ebook Studio ใช้สำหรับสร้าง จัดการ และเผยแพร่ E-book จากไฟล์ PDF ที่คุณมีสิทธิ์ใช้งานเท่านั้น")}
          </Typography>
          <Typography component="p" variant="body2" mb={1.5}>
            {__("คุณรับรองว่าไฟล์ ข้อความ รูปภาพ และข้อมูลที่อัปโหลดไม่ละเมิดกฎหมาย ลิขสิทธิ์ เครื่องหมายการค้า หรือสิทธิของบุคคลอื่น")}
          </Typography>
          <Typography component="p" variant="body2" mb={1.5}>
            {__("ระบบอาจประมวลผลไฟล์และข้อความเพื่อสร้างภาพหน้า สารบัญ ค้นหา และตรวจทานด้วย AI เฉพาะเมื่อคุณสั่งใช้งานฟีเจอร์ที่เกี่ยวข้อง")}
          </Typography>
          <Typography component="p" variant="body2" mb={1.5}>
            {__("บัญชีของคุณใช้ Google Sign-In เพื่อยืนยันตัวตน และข้อมูลบัญชีพื้นฐานจะถูกใช้เพื่อรักษาความปลอดภัยและแสดงผลในระบบ")}
          </Typography>
          <Typography component="p" variant="body2">
            {__("บริการนี้ให้ใช้งานตามสภาพจริง ผู้ใช้ต้องตรวจสอบความถูกต้องของเนื้อหาและผลลัพธ์ก่อนนำไปเผยแพร่")}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTermsOpen(false)}>{__("ปิด")}</Button>
        <Button
          variant="contained"
          onClick={() => {
            updateTermsAccepted(true);
            setTermsOpen(false);
          }}
        >
          {__("ยอมรับ")}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
